/* eslint-disable max-lines */
import {
  DataVersion,
  Location,
  NeighbourAlarmsResponse,
  UnifiedAlarm,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  buildDoorToCameraMap,
  foldDoorAlarmsOntoCameras,
  isUnifiedDataVersion,
  trackEvent,
  usePrevious,
} from '@hakimo-ui/hakimo/util';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MediaClock } from '../../frame-sequence-player';
import { isMinorAlarm } from '../../utils/isMinorAlarm';
import { AlarmPreloaderConfiguration, SCAN_GAP_SKIP_MS } from '../constants';
import { useAlarmState } from '../hooks/useAlarmState';
import { useFramesBufferingGate } from '../hooks/useFramesBufferingGate';
import {
  CameraAlarmTracker,
  EnterpriseAlarmDetails,
  TimelineBounds,
} from '../types';
import {
  dedupeLocationAlarmsByCameraAndTime,
  dedupeScanAlarmsByCameraAndTime,
  getEnterpriseAlarmDetails,
  getStartAndEndTime,
  groupHasMedia as computeGroupHasMedia,
} from '../utils';
import { AlarmPreloader, PreloaderState } from './AlarmPreloader';
import {
  AlarmGroupCameraContext,
  AlarmGroupPlaybackContext,
  AlarmGroupStaticContext,
  AlarmGroupTimeContext,
} from './contexts';
import { getTimelineGaps } from './util';

// Re-export hooks for consumers
export {
  useAlarmGroupCamera,
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from './contexts';

const defaultTimelineBounds: TimelineBounds = {
  alarmGroupStartTime: 0,
  alarmGroupEndTime: 0,
  totalTimelineStartTime: 0,
  totalTimelineEndTime: 0,
};

interface Props {
  alarmGroupId: string;
  alarms: UnifiedAlarm[];
  location: Location;
  children: ReactNode;
  product: UnifiedMonitoringProduct;
  /** Group-level TAP score (from UnifiedAlarmGroup.tapScore). */
  tapScore?: number;
  /** ISO timestamp of the alarm group (from UnifiedAlarmGroup.timestamp). */
  timestamp?: string;
  /** Alarm type (from UnifiedAlarmGroup.locationAlarmType). */
  locationAlarmType?: string;
  /** See AlarmGroupStaticContextType.dataVersion. */
  dataVersion?: DataVersion;
  /** See AlarmGroupStaticContextType.sourceGroupId. */
  sourceGroupId?: string;
}

const defaultPreloaderState: PreloaderState = {
  showCameraIds: new Set<string>(),
  preloadCameraIds: new Set<string>(),
  camToAlarmTracker: new Map<string, CameraAlarmTracker>(),
};

export function AlarmGroupProvider(props: Props) {
  const {
    alarms: rawAlarms,
    children,
    location,
    alarmGroupId,
    product,
    tapScore,
    timestamp,
    locationAlarmType,
    dataVersion,
    sourceGroupId,
  } = props;
  // An enterprise alarm is raised on a door and only some of its copies carry
  // the camera. Learn door -> camera from the unfiltered alarms and move the
  // door-only copies onto it before anything below keys on `cameraId`, so the
  // group shows one row, tile and count per camera.
  const doorToCamera = useMemo(
    () => buildDoorToCameraMap(rawAlarms),
    [rawAlarms]
  );
  const alarms = useMemo(
    () => foldDoorAlarmsOntoCameras(rawAlarms, doorToCamera),
    [rawAlarms, doorToCamera]
  );
  // Consolidated preloader state - updated via single listener to avoid multiple render cycles
  const [preloaderState, setPreloaderState] = useState<PreloaderState>(
    defaultPreloaderState
  );
  const [showMinorAlarms, setShowMinorAlarms] = useState<boolean>(true);

  const preloaderRef = useRef<AlarmPreloader | null>(null);
  const initialAlarmsRef = useRef<UnifiedAlarm[]>(alarms);
  // initial Alarm group start time and end time
  const initialAlarmGroupBoundsRef = useRef<number[]>(
    getStartAndEndTime(alarms)
  );
  const [seekerTimestamp, setSeekerTimestamp] = useState(
    initialAlarmGroupBoundsRef.current[0]
  );
  // Continuous playback clock — the single source of truth for time. The React
  // `seekerTimestamp` above is a throttled sample of it (for the timeline/video),
  // while frame views read the clock directly via rAF for smooth, alias-free play.
  const clockRef = useRef<MediaClock | null>(null);
  if (!clockRef.current) {
    clockRef.current = new MediaClock({
      position: initialAlarmGroupBoundsRef.current[0],
    });
  }
  const hasFrames = useMemo(
    () => alarms.some((a) => a.media?.type === 'frames'),
    [alarms]
  );
  // Scan-vs-rg UI discriminator across the v2/v3 split (see
  // AlarmGroupStaticContext.isScanLike). v3 delivers scan alarms as
  // remote-guarding, so detect them via frames; v2 is exactly product === SCAN.
  const isScanLike =
    product === UnifiedMonitoringProduct.SCAN ||
    (isUnifiedDataVersion(dataVersion) && hasFrames);
  const hasVideos = useMemo(
    () => alarms.some((a) => a.media?.type === 'video'),
    [alarms]
  );

  // Buffering comes from the frames gate (for frame alarms), the preloader
  // (for video alarms), or both. A group with neither is not "loading" — that
  // is the enterprise no-media case, which must resolve to false or the view
  // would spin forever.
  const {
    isBuffering: isFramesBuffering,
    reportFramesReady,
    resetGate: resetFramesGate,
  } = useFramesBufferingGate(clockRef.current, hasFrames);
  const [isPreloaderBuffering, setPreloaderBuffering] = useState(true);
  const isBuffering =
    (hasFrames && isFramesBuffering) || (hasVideos && isPreloaderBuffering);
  const [isSeekerPaused, setIsSeekerPaused] = useState(false);
  const [neighbourAlarms, setNeighbourAlarms] = useState<UnifiedAlarm[]>([]);
  const [timelineGaps, setTimelineGaps] = useState<Array<[number, number]>>([]);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [hasLoadedNeighbourAlarms, setHasLoadedNeighbourAlarms] =
    useState(false);

  const toggleSeekerPaused = useCallback(() => {
    trackEvent('alarm_group_playback_control', {
      alarmGroupId,
      product,
      action: isSeekerPaused ? 'play' : 'pause',
    });
    setIsSeekerPaused((prev) => !prev);
  }, [alarmGroupId, product, isSeekerPaused]);
  const toggleShowMinorAlarms = useCallback(
    (val: boolean) => {
      trackEvent('alarm_group_toggle_minor_alarms', {
        alarmGroupId,
        product,
        show: val,
      });
      setShowMinorAlarms(val);
    },
    [alarmGroupId, product]
  );
  const updatePlaybackSpeed = useCallback(
    (speed: number) => setPlaybackSpeed(speed),
    []
  );

  // Neighbours are fetched by door too, so they take the same door -> camera
  // mapping as the group's own alarms.
  const foldedNeighbourAlarms = useMemo(
    () => foldDoorAlarmsOntoCameras(neighbourAlarms, doorToCamera),
    [neighbourAlarms, doorToCamera]
  );

  // Hide minors when showMinorAlarms is false (isMinorAlarm covers scan-shaped
  // and location alarms alike), then collapse duplicates. Scan-shaped data,
  // which v3 delivers as remote guarding (isScanLike), dedupes as scan;
  // remote guarding and enterprise dedupe as location alarms.
  const filterAndDedupe = useCallback(
    (list: UnifiedAlarm[]) => {
      const filtered = showMinorAlarms
        ? list
        : list.filter((alarm) => !isMinorAlarm(alarm));
      return isScanLike
        ? dedupeScanAlarmsByCameraAndTime(filtered)
        : dedupeLocationAlarmsByCameraAndTime(filtered);
    },
    [isScanLike, showMinorAlarms]
  );

  const updatedNeighbourAlarms = useMemo(
    () => filterAndDedupe(foldedNeighbourAlarms),
    [filterAndDedupe, foldedNeighbourAlarms]
  );

  const updatedAlarms = useMemo(
    () => filterAndDedupe(alarms),
    [filterAndDedupe, alarms]
  );

  const {
    alarmCameraIds,
    camToDetailsMap,
    tenantAdditionalCameraIds,
    extraCamDetailsMap,
    updateExtraCamFromTenant,
    alarmToDetailsMap,
  } = useAlarmState(updatedAlarms, updatedNeighbourAlarms);

  // The neighbour API matches enterprise alarms on the door, not the camera.
  // Taken from the unfiltered alarms, so hiding minors cannot drop the door.
  const neighbourSourceEntityIds = useMemo(
    () =>
      Array.from(
        new Set([...alarmCameraIds, ...Array.from(doorToCamera.keys())])
      ),
    [alarmCameraIds, doorToCamera]
  );

  const [timelineBounds, setTimelineBounds] = useState<TimelineBounds>(
    defaultTimelineBounds
  );

  const handleNeighbourAlarmsResponse = useCallback(
    (data: NeighbourAlarmsResponse) => {
      // A window with no neighbours comes back without `alarms` at all, so
      // concat planted an undefined entry and forEach threw. Empty is the
      // meaningful default: the bounds below then simply stay put.
      const newAlarms = data?.alarms ?? [];
      setNeighbourAlarms((prev) => prev.concat(newAlarms));
      setHasLoadedNeighbourAlarms(true);
      // Bound the timeline by where the alarms are shown: a door alarm with no
      // video arrives stamped with the time it was loaded, not its alarm time.
      const shownAlarms = foldDoorAlarmsOntoCameras(newAlarms, doorToCamera);
      const newAlarmtimestamps: number[] = [];
      shownAlarms.forEach((alarm) => {
        newAlarmtimestamps.push(alarm.alarmStartTime);
        newAlarmtimestamps.push(alarm.alarmEndTime);
      });
      // The same response that omits `alarms` also omits fields on the alarms
      // it does carry, and one missing timestamp makes Math.min NaN. The bounds
      // come back as `prev` on the next response, so that NaN would survive
      // every later call and take the timeline and seek math with it. Dropping
      // the non-finite values leaves an empty spread, which the infinities
      // below turn into a no-op.
      const finiteTimestamps = newAlarmtimestamps.filter(Number.isFinite);
      const newAlarmsLowestTime = Math.min(...finiteTimestamps);
      const newAlarmsHighestTime = Math.max(...finiteTimestamps);
      const { startTimestamp, endTimestamp } = data ?? {};
      setTimelineBounds((prev) => {
        const newTotalTimelineStartTime = Math.min(
          prev.totalTimelineStartTime,
          Math.min(startTimestamp ?? Infinity, newAlarmsLowestTime)
        );
        const newTotalTimelineEndTime = Math.max(
          prev.totalTimelineEndTime,
          Math.max(endTimestamp ?? -Infinity, newAlarmsHighestTime)
        );
        return {
          ...prev,
          totalTimelineStartTime: newTotalTimelineStartTime,
          totalTimelineEndTime: newTotalTimelineEndTime,
        };
      });
    },
    [doorToCamera]
  );

  const resetTimeline = useCallback(
    (startTime?: number) => {
      const resetTime = startTime ?? timelineBounds.alarmGroupStartTime;
      setTimelineBounds((prev) => ({
        ...prev,
        totalTimelineStartTime: startTime ?? prev.alarmGroupStartTime,
        totalTimelineEndTime: prev.alarmGroupEndTime,
      }));
      clockRef.current?.seek(resetTime);
      preloaderRef.current?.moveTo(resetTime);
      setSeekerTimestamp(resetTime);
      setNeighbourAlarms([]);
      setHasLoadedNeighbourAlarms(false);
      resetFramesGate();
    },
    [timelineBounds.alarmGroupStartTime, resetFramesGate]
  );

  useEffect(() => {
    const totalTimelineStartTime = timelineBounds.totalTimelineStartTime;
    const totalTimelineEndTime = timelineBounds.totalTimelineEndTime;
    const newTimelineGaps = getTimelineGaps(
      [...updatedAlarms, ...updatedNeighbourAlarms],
      totalTimelineStartTime,
      totalTimelineEndTime
    );
    setTimelineGaps(newTimelineGaps);
  }, [
    updatedAlarms,
    updatedNeighbourAlarms,
    timelineBounds.totalTimelineEndTime,
    timelineBounds.totalTimelineStartTime,
  ]);

  // seekertimestamp, alarmgroup start & end time, total timeline start & end time,

  useEffect(() => {
    if (updatedAlarms.length === 0) return;
    const [alarmGroupStartTime, alarmGroupEndTime] =
      getStartAndEndTime(updatedAlarms);
    const [totalTimelineStartTime, totalTimelineEndTime] = [
      alarmGroupStartTime,
      alarmGroupEndTime,
    ];
    setTimelineBounds((prev) => {
      const newVal = { ...prev, alarmGroupStartTime, alarmGroupEndTime };
      if (
        prev.totalTimelineStartTime === 0 ||
        totalTimelineStartTime < prev.totalTimelineStartTime
      ) {
        newVal['totalTimelineStartTime'] = totalTimelineStartTime;
      }
      if (
        prev.totalTimelineEndTime === 0 ||
        totalTimelineEndTime > prev.totalTimelineEndTime
      ) {
        newVal.totalTimelineEndTime = totalTimelineEndTime;
      }
      return newVal;
    });
  }, [updatedAlarms]);

  const prevShowMinorAlarms = usePrevious(showMinorAlarms);

  useEffect(() => {
    const isHidingMinorAlarms =
      prevShowMinorAlarms === true && showMinorAlarms === false;
    if (
      isHidingMinorAlarms &&
      !hasLoadedNeighbourAlarms &&
      updatedAlarms.length > 0
    ) {
      const [newStartTime] = getStartAndEndTime(updatedAlarms);
      resetTimeline(newStartTime);
    }
  }, [
    showMinorAlarms,
    hasLoadedNeighbourAlarms,
    prevShowMinorAlarms,
    updatedAlarms,
    resetTimeline,
  ]);

  useEffect(() => {
    // Mount-only: `hasVideos` resolves asynchronously, so keying this effect
    // on it would rebuild the preloader mid-session and re-run moveTo(). The
    // buffer listeners are inert until there is video, and `isBuffering`
    // already gates on `hasVideos`.
    preloaderRef.current = new AlarmPreloader(
      initialAlarmsRef.current,
      AlarmPreloaderConfiguration
    );

    const preloader = preloaderRef.current;
    preloader.onBufferReady(() => setPreloaderBuffering(false));
    preloader.onBufferNotReady(() => setPreloaderBuffering(true));
    // Use consolidated state change listener to batch React updates
    // This prevents multiple render cycles when preloader emits multiple changes
    preloader.onStateChange((state) => setPreloaderState(state));
    // Initial load at time 0
    preloader.moveTo(initialAlarmGroupBoundsRef.current[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (preloaderRef.current) {
      preloaderRef.current.updateAlarms([
        ...updatedAlarms,
        ...updatedNeighbourAlarms,
      ]);
    }
  }, [updatedAlarms, updatedNeighbourAlarms]);

  const updateAlarmLoadState = useCallback(
    (alarmId: string, isReady: boolean) => {
      if (preloaderRef.current) {
        preloaderRef.current.updateAlarmLoadState(alarmId, isReady);
      }
    },
    []
  );

  const seekTo = useCallback((t: number) => {
    const clock = clockRef.current;
    if (!clock) return;
    clock.seek(t); // snaps out of gaps + clamps to bounds
    const newTime = clock.getTime();
    preloaderRef.current?.moveTo(newTime);
    setSeekerTimestamp(newTime);
    // No explicit re-buffer here: on a far seek the target frame isn't decoded yet,
    // so cameras report not-ready and the clock stalls until it lands (video-like);
    // a near seek to already-decoded footage never stalls. See reportFramesReady.
  }, []);

  // Keep the clock's bounds and gaps in sync. For frames (SCAN) only large gaps
  // are skipped so sub-frame-interval gaps between consecutive alarms play through
  // continuously; video keeps its exact gap behaviour.
  useEffect(() => {
    const { totalTimelineStartTime, totalTimelineEndTime } = timelineBounds;
    if (totalTimelineEndTime > 0) {
      clockRef.current?.setBounds(totalTimelineStartTime, totalTimelineEndTime);
    }
  }, [timelineBounds]);

  useEffect(() => {
    const clockGaps = hasFrames
      ? timelineGaps.filter(([s, e]) => e - s > SCAN_GAP_SKIP_MS)
      : timelineGaps;
    clockRef.current?.setGaps(clockGaps);
  }, [timelineGaps, hasFrames]);

  // Playback loop: while playing, advance the (continuous) clock and sample it
  // into seekerTimestamp + the preloader at ~10Hz. Frame views read the clock
  // directly via rAF, so frames stay smooth regardless of this sample rate.
  useEffect(() => {
    const clock = clockRef.current;
    if (!clock) return;
    if (isBuffering || isSeekerPaused) {
      clock.pause();
      return;
    }
    clock.play(playbackSpeed);
    // NB: we intentionally do NOT tear down at the end. `settle()` re-pins the clock
    // to `max` every tick (so it holds the last frame with no phantom drift) while the
    // play intent stays live — so seeking back, or new alarms extending the bounds,
    // resume playback automatically until the user explicitly pauses.
    let last = -1;
    const timer = window.setInterval(() => {
      clock.settle(); // skip over gaps / hold at the end
      const t = clock.getTime();
      if (t === last) return; // idle at the end (or in a gap): nothing moved
      last = t;
      preloaderRef.current?.moveTo(t);
      setSeekerTimestamp(t);
    }, 100);
    return () => window.clearInterval(timer);
  }, [isBuffering, isSeekerPaused, playbackSpeed]);

  const allCombinedAlarms = useMemo(() => {
    return [...updatedAlarms, ...updatedNeighbourAlarms];
  }, [updatedAlarms, updatedNeighbourAlarms]);

  const isEnterpriseAlarmGroup =
    product === UnifiedMonitoringProduct.ENTERPRISE;
  // Reactive — media can arrive in later WebSocket batches, so we check the
  // live alarms prop rather than the initial snapshot.
  const groupHasMedia = useMemo(() => computeGroupHasMedia(alarms), [alarms]);
  // Enterprise details. Ref-derived fields (employee, alarm_source_system, tap)
  // are stable after mount; tapScore, timestamp, and locationAlarmType are
  // props that may update via WebSocket.
  const enterpriseAlarmDetails = useMemo<EnterpriseAlarmDetails | undefined>(
    () => {
      if (!isEnterpriseAlarmGroup) return undefined;
      const first = initialAlarmsRef.current[0];
      if (!first) return undefined;
      return getEnterpriseAlarmDetails(first, {
        tapScore,
        timestamp,
        locationAlarmType,
      });
    },
    [isEnterpriseAlarmGroup, tapScore, timestamp, locationAlarmType] // first's fields are ref-stable
  );
  const staticContextValue = useMemo(
    () => ({
      alarmGroupId,
      location,
      product,
      alarmCameraIds,
      neighbourSourceEntityIds,
      tenantAdditionalCameraIds,
      groupHasMedia,
      groupHasFrames: hasFrames,
      enterpriseAlarmDetails,
      locationAlarmType,
      clock: clockRef.current ?? undefined,
      dataVersion,
      isScanLike,
      sourceGroupId,
    }),
    [
      alarmGroupId,
      location,
      product,
      alarmCameraIds,
      neighbourSourceEntityIds,
      tenantAdditionalCameraIds,
      groupHasMedia,
      hasFrames,
      enterpriseAlarmDetails,
      dataVersion,
      isScanLike,
      sourceGroupId,
      locationAlarmType,
    ]
  );

  // Time context - high frequency updates (every second during playback)
  const timeContextValue = useMemo(
    () => ({
      seekerTimestamp,
      isSeekerPaused,
      playbackSpeed,
      seekTo,
      toggleSeekerPaused,
      updatePlaybackSpeed,
    }),
    [
      seekerTimestamp,
      isSeekerPaused,
      playbackSpeed,
      seekTo,
      toggleSeekerPaused,
      updatePlaybackSpeed,
    ]
  );

  // Camera context - updates on window changes
  const cameraContextValue = useMemo(
    () => ({
      showCameraIds: preloaderState.showCameraIds,
      preloadCameraIds: preloaderState.preloadCameraIds,
      camToDetailsMap,
      camToAlarmTracker: preloaderState.camToAlarmTracker,
      extraCamDetailsMap,
      updateExtraCamFromTenant,
      alarmToDetailsMap,
    }),
    [
      preloaderState,
      camToDetailsMap,
      extraCamDetailsMap,
      updateExtraCamFromTenant,
      alarmToDetailsMap,
    ]
  );

  // Playback context - buffer and timeline state
  const playbackContextValue = useMemo(
    () => ({
      isBuffering,
      timelineBounds,
      timelineGaps,
      updateAlarmLoadState,
      allCombinedAlarms,
      showMinorAlarms,
      toggleShowMinorAlarms,
      handleNeighbourAlarmsResponse,
      resetTimeline,
      reportFramesReady,
    }),
    [
      isBuffering,
      timelineBounds,
      timelineGaps,
      updateAlarmLoadState,
      allCombinedAlarms,
      showMinorAlarms,
      toggleShowMinorAlarms,
      handleNeighbourAlarmsResponse,
      resetTimeline,
      reportFramesReady,
    ]
  );

  return (
    <AlarmGroupStaticContext.Provider value={staticContextValue}>
      <AlarmGroupTimeContext.Provider value={timeContextValue}>
        <AlarmGroupCameraContext.Provider value={cameraContextValue}>
          <AlarmGroupPlaybackContext.Provider value={playbackContextValue}>
            {children}
          </AlarmGroupPlaybackContext.Provider>
        </AlarmGroupCameraContext.Provider>
      </AlarmGroupTimeContext.Provider>
    </AlarmGroupStaticContext.Provider>
  );
}

export default AlarmGroupProvider;
